import { registerFeature } from '../operationRegistry';
import { api } from '../../api';
import { useActiveCartStore } from '../../../hooks/useCartActions';

export function registerCartOperations(): void {
  registerFeature({
    name: 'cart',
    invalidateKeys: [['carts']],
    operations: [
      {
        type: 'CART_CREATE',
        collapseRule: 'dropIfParentDeleted',
        async execute(payload) {
          return api<{ id: number }>('/carts', {
            method: 'POST',
            body: JSON.stringify({ name: payload.name }),
          });
        },
        extractId: (res: unknown) => (res as { id: number }).id,
        // A document created while offline is active under its tempId. Once
        // the queue syncs it, that id disappears from the cart list and the
        // guard in useCartActions would drop the user back into the oldest
        // document — so move the selection over to the server id
        // (VIVATISPDB-443).
        onIdResolved: (tempId, realId) => {
          const { activeCartId, setActiveCartId } = useActiveCartStore.getState();
          if (String(activeCartId) === tempId) {
            setActiveCartId(realId);
          }
        },
      },
      {
        type: 'CART_RENAME',
        collapseRule: 'lastWins',
        collapseKeyFn: (p) => `cart:${p.cartId}`,
        async execute(payload) {
          await api(`/carts/${payload.cartId}`, {
            method: 'PUT',
            body: JSON.stringify({ name: payload.name }),
          });
          return undefined;
        },
      },
      {
        type: 'CART_ARCHIVE',
        collapseRule: 'dropIfParentDeleted',
        parentIdField: 'cartId',
        async execute(payload) {
          await api(`/carts/${payload.cartId}`, {
            method: 'PUT',
            body: JSON.stringify({ is_active: false }),
          });
          return undefined;
        },
      },
      {
        type: 'CART_DELETE',
        collapseRule: 'none',
        isEntityDelete: true,
        entityIdField: 'cartId',
        async execute(payload) {
          await api(`/carts/${payload.cartId}`, { method: 'DELETE' });
          return undefined;
        },
      },
      {
        type: 'CART_ADD_PRODUCT',
        collapseRule: 'cancelPair',
        cancelPairType: 'CART_REMOVE_PRODUCT',
        collapseKeyFn: (p) => `cartprod:${p.cartId}:${p.productId}`,
        parentIdField: 'cartId',
        async execute(payload) {
          await api(`/carts/${payload.cartId}/products`, {
            method: 'POST',
            body: JSON.stringify({ product_id: payload.productId }),
          });
          return undefined;
        },
      },
      {
        type: 'CART_ADD_PRODUCTS_BATCH',
        collapseRule: 'none',
        parentIdField: 'cartId',
        async execute(payload) {
          await api(`/carts/${payload.cartId}/products`, {
            method: 'POST',
            body: JSON.stringify({ product_ids: payload.productIds }),
          });
          return undefined;
        },
      },
      {
        type: 'CART_REMOVE_PRODUCT',
        collapseRule: 'cancelPair',
        cancelPairType: 'CART_ADD_PRODUCT',
        collapseKeyFn: (p) => `cartprod:${p.cartId}:${p.productId}`,
        parentIdField: 'cartId',
        async execute(payload) {
          await api(`/carts/${payload.cartId}/products/${payload.productId}`, {
            method: 'DELETE',
          });
          return undefined;
        },
      },
    ],
  });
}
